/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {SpatialUtilService} from '../../spatial-util.service';
import {AngleUtilService} from './angle-util.service';
import {catchError, of, tap} from 'rxjs';
import {AppConfigService} from '../../../../common/service/app-config.service';
import {AlertModalService} from '../../../../common/component/modal';
import {EsriModuleService} from '../../../esri-nlr/services/esri-module.service';

@Injectable({
    providedIn: 'root'
})
export class AngleUpdateService {
    title = 'AngleUpdateService';

    constructor(
        private alertModalService: AlertModalService,
        private appConfigService: AppConfigService,
        private angleUtilService: AngleUtilService,
        private esriModuleService: EsriModuleService,
        private spatialUtilService: SpatialUtilService
    ) {}

    updateAngleTypesIfRequired(linesAfterUpdate: any[], linesBeforeUpdate: any[]) {
        console.debug(`${this.title} - updateAngleTypesIfRequired`);

        const linesWithModifiedType = linesAfterUpdate.filter((lineAfterUpdate: any) =>
            linesBeforeUpdate.some(
                lineBeforeUpdate =>
                    lineBeforeUpdate.attributes.id === lineAfterUpdate.attributes.id &&
                    lineBeforeUpdate.attributes.type !== lineAfterUpdate.attributes.type
            )
        );

        if (linesWithModifiedType.length > 0) {
            const relatedAngles = this.findRelatedAnglesToUpdatedLines(linesWithModifiedType);
            const lines = this.spatialUtilService.getLines();
            const lineMap = lines.reduce((acc: any, line: any) => {
                acc[line.attributes.id] = line;
                return acc;
            }, {});

            const anglesToBeUpdated = [];

            relatedAngles.forEach((angle: any) => {
                const angleFromLine = lineMap[angle.attributes.anglefromlineid];
                const angleToLine = lineMap[angle.attributes.angletolineid];

                const newAngleType = this.angleUtilService.getAngleTypeByLines(angleFromLine, angleToLine);

                if (`${angle.attributes.type}` !== newAngleType) {
                    const updatedAngle = new this.esriModuleService.Graphic(angle.toJson());
                    updatedAngle.attributes.type = newAngleType;
                    anglesToBeUpdated.push(updatedAngle);
                }
            });

            if (anglesToBeUpdated.length > 0) {
                return this.applyAngleUpdates(anglesToBeUpdated);
            }
        }

        return of(null);
    }

    updateRelatedAngleAttributes(anglesAfterUpdate: any[]) {
        console.debug(`${this.title} - updateRelatedAngleAttributes`);

        const angles = this.spatialUtilService
            .getAngles()
            .filter((f: {attributes: {enlargementid?: any; enlargementids?: any}}) => f.attributes.enlargementid || f.attributes.enlargementids);

        if (angles.length === 0) {
            return of(null);
        }

        const relatedAngles = this.getAllRelatedAngles(angles, anglesAfterUpdate);
        const anglesToBeUpdated = this.findAndUpdateRelatedAngleAttributes(relatedAngles);

        if (anglesToBeUpdated.length === 0) {
            return of(null);
        }

        this.spatialUtilService.projectFeatures(anglesToBeUpdated);

        return this.spatialUtilService.applyEdits('angle', null, anglesToBeUpdated, null).pipe(
            tap((res: any) => {
                console.debug(`${this.title} : angle updates:`, res.updates);

                if (res.updates[0].success) {
                    console.debug(`${this.title} : angles updated successfully`);
                } else {
                    console.error(res.updates[0].error);
                    this.alertModalService.createAlertMessage(
                        `Error updating angles: ${(res.updates[0].error && res.updates[0].error.message) || ''}`,
                        'Ok'
                    );
                }
            }),
            catchError(err => this.alertModalService.createAlertMessage(`Error updating angle features. Error : ${err}`, 'Ok'))
        );
    }

    private findRelatedAnglesToUpdatedLines(lines: any[]) {
        console.debug(`${this.title} - findRelatedAnglesToUpdatedLines`);
        const lineIds = lines.map(line => line.attributes.id).filter((v, i, a) => i === a.indexOf(v));
        return this.spatialUtilService
            .getAngles()
            .filter(
                (f: {attributes: {anglefromlineid?: any; angletolineid?: any}}) =>
                    lineIds.includes(f.attributes.anglefromlineid) || lineIds.includes(f.attributes.angletolineid)
            );
    }

    private applyAngleUpdates(angles: any[]) {
        const logPrefix = `${this.title} - applyAngleUpdates`;
        console.debug(logPrefix);

        return this.spatialUtilService.applyEdits('angle', null, angles, null).pipe(
            tap((res: any) => {
                console.debug(`${logPrefix} - angle updates:`, res.updates);

                if (res.updates[0].success) {
                    console.debug(`${logPrefix} - angles updated successfully`);
                } else {
                    console.error(`${logPrefix} - error:`, res.updates[0].error);
                }
            }),
            catchError(err => this.alertModalService.createAlertMessage(`Error updating angle features. Error : ${err}`, 'Ok'))
        );
    }

    private getAllRelatedAngles(allAngles: any[], updatedAngles: any[]) {
        console.debug(`${this.title} - getAllRelatedAngles`);

        const output = [];

        updatedAngles.forEach((updatedAngle: {attributes: {id?: any; parentid?: any}}) => {
            const enlargementAngles = allAngles.filter(
                (angle: {attributes: {id?: any; parentid?: any}}) =>
                    (updatedAngle.attributes.id !== angle.attributes.id && updatedAngle.attributes.id === angle.attributes.parentid) ||
                    (updatedAngle.attributes.parentid !== null &&
                        angle.attributes.parentid !== null &&
                        updatedAngle.attributes.parentid === angle.attributes.parentid)
            );

            const mainPlanAngles = allAngles.filter(angle => updatedAngle.attributes.parentid === angle.attributes.id);

            output.push({updatedAngle: updatedAngle, relatedAngles: [...mainPlanAngles, ...enlargementAngles]});
        });

        return output;
    }

    private findAndUpdateRelatedAngleAttributes(relatedAngles: any[]) {
        console.debug(`${this.title} - findAndUpdateRelatedAngleAttributes`);

        const output = [];

        relatedAngles.forEach((record: any) => {
            record.relatedAngles.forEach((relatedAngle: any) => {
                if (record.updatedAngle.attributes.id === relatedAngle.attributes.id) {
                    return;
                }

                for (const [key, value] of Object.entries(record.updatedAngle.attributes) as any[]) {
                    if (!this.appConfigService.getDesignProperties().EVENTS.ANGLE_UPDATE.IRRELEVANT_ATTRIBUTES_ON_UPDATE.includes(key)) {
                        if (key === 'hiddenonenlargements') {
                            relatedAngle.attributes.hiddenonenlargements = value?.includes(`[${relatedAngle.attributes.enlargementid}]`)
                                ? `[${relatedAngle.attributes.enlargementid}]`
                                : null;
                        } else {
                            relatedAngle.attributes[key] = value;
                        }
                    }
                }

                output.push(relatedAngle);
            });
        });

        return output;
    }
}
